import { InfowindowContentBuilder } from './infoWindowContentBuilder';

type InfoWindowBuilderProps = {
  content: InfowindowContentBuilder[];
  marker: google.maps.Marker;
  map: google.maps.Map;
  style?: string;
  index?: number;
  actions?: { id: string; action: () => void; description?: string }[];
};

export class InfoWindowSingleton {
  private static instance: InfoWindowSingleton;
  private createdAt: Date = new Date();

  constructor() {
    if (InfoWindowSingleton.instance) {
      return InfoWindowSingleton.instance;
    }

    this.createdAt = new Date();

    InfoWindowSingleton.instance = this;
  }

  getInfo() {
    console.log(`Infowindow instance created at ${this.createdAt}`);
  }
}

export class InfowindowBuilder extends InfoWindowSingleton {
  private _content: InfowindowContentBuilder[];
  private _contentIndex: number;
  private _style: string;
  private _infoWindow: google.maps.InfoWindow = new google.maps.InfoWindow();
  private _marker: google.maps.Marker;
  private _map: google.maps.Map;
  private _baseStyle = `<style>
  .gm-style-iw-t .gm-style-iw.gm-style-iw-c {
    padding: 0
  }

  .gm-style-iw-t .gm-style-iw.gm-style-iw-c  .gm-style-iw-d {
    overflow: auto !important;
  }

  .gm-style-iw-t .gm-style-iw.gm-style-iw-c button.gm-ui-hover-effect {
    top: 20px !important;
    right: 20px !important;

    width: 16px !important;
    height: 16px !important;
  }

  .gm-style-iw-t .gm-style-iw.gm-style-iw-c span {
    width: 16px !important;
    height: 16px !important;

    margin: 0 !important;
  }
  </style>
  `;
  private _actions: { id: string; action: () => void; description?: string }[];

  constructor({
    content,
    marker,
    map,
    style = '',
    index = 0,
    actions = [{ id: '', action: () => {}, description: '' }],
  }: InfoWindowBuilderProps) {
    super();
    this._content = content;
    this._contentIndex = index;
    this._marker = marker;
    this._map = map;
    this._style = `<style>${style}</style>`;
    this._actions = actions;

    this.build();
  }

  /**
   * Change infoWindow content
   * @param content
   * @example setContent([new InfowindowContentBuilder({ header: 'header', body: 'body', footer: 'footer' })])
   */
  setContent(content: InfowindowContentBuilder[]) {
    this._content = content;
  }

  /**
   * Get infoWindow content
   */
  getContent() {
    return this._content;
  }

  /**
   * Set infoWindow style
   * @param style
   * @exmaple setStyle('body { background-color: red; }')
   */
  setStyle(style: string) {
    this._style = `<style>${style}</style>`;
  }

  /**
   * Open infoWindow
   * @example open()
   */
  open() {
    this._infoWindow.close();
    this.build();
    this.getInfo();
    this._infoWindow.open(this._map, this._marker);
  }

  /**
   * Close infoWindow
   * @example close()
   */
  close() {
    this._infoWindow.close();
  }

  /**
   * Get infoWindow instance
   * @returns google.maps.InfoWindow
   */
  getInfowindow() {
    return this._infoWindow;
  }

  /**
   * Add action to infoWindow elements
   * @param event
   */
  private _listener = (event: MouseEvent) => {
    this._actions.forEach(({ id, action }) => {
      const domId = (event?.target as HTMLElement)?.id;
      if (domId === id) {
        action();
      }
    });
  };

  /**
   * Add actions to infoWindow elements
   * @example addActionsToId()
   */
  addActionsToId() {
    this._infoWindow.addListener('domready', () => {
      this._actions.forEach(({ id }) => {
        const dom = document.getElementById(id);
        if (dom) {
          dom.addEventListener('click', this._listener);
        }
      });
    });
  }

  /**
   * Buidls next infowindow content
   * @example nextContent()
   */
  nextContent() {
    if (this._contentIndex < this._content.length - 1) {
      this._contentIndex++;
      this.build();
    }
  }

  /**
   * Builds previous infowindow content
   * @example previousContent()
   */
  previousContent() {
    if (this._contentIndex > 0) {
      this._contentIndex--;
      this.build();
    }
  }

  /**
   * Get current infowindow content index
   * @returns number
   */
  getContentIndex() {
    return this._contentIndex;
  }

  /**
   * Builds infowindow content
   * @example build()
   */
  build() {
    this.addActionsToId();
    const iw = `${this._baseStyle}\n${this._style}\n${this._content[
      this._contentIndex
    ].getContent()}`;
    this._infoWindow.setContent(iw);
  }
}
